import { useState, useCallback } from "react";
import { toast } from "sonner";

/**
 * Hook condiviso per upload PARALLELO multi-file con drag & drop e progress.
 *
 * @param {Object} cfg
 * @param {(file: File, onProgress: (pct:number, loaded:number, total:number) => void) => Promise<any>} cfg.uploadOne
 *        Carica UN file. La funzione DEVE chiamare `onProgress` durante l'upload (es. via axios onUploadProgress).
 * @param {() => Promise<void>} [cfg.onAfter] - Callback dopo che TUTTI i file sono stati processati.
 * @param {() => Set<string>} [cfg.getExistingFilenames] - Callback opzionale che ritorna l'insieme dei
 *        nomi file (lowercase) già presenti per il contesto corrente (es. fornitore). Quando fornito,
 *        i file con quel nome vengono SALTATI automaticamente (status="skipped"), senza upload.
 * @param {string} [cfg.successLabel] - Etichetta singolare (default: "file caricato").
 * @param {string} [cfg.successLabelPlural] - Plurale (default: "file caricati").
 */
export function useFileUpload({ uploadOne, onAfter, getExistingFilenames, successLabel = "file caricato", successLabelPlural = "file caricati" }) {
  const [uploading, setUploading] = useState(false);
  // perFile: Map<file.name, { name, size, loaded, status: "uploading"|"done"|"error"|"skipped", error?, skippedReason? }>
  const [perFile, setPerFile] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const uploadFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.size > 0);
    if (files.length === 0) return;
    // Snapshot dei nomi file già caricati (lowercased) per dedup.
    let existing = null;
    if (typeof getExistingFilenames === "function") {
      try {
        const v = getExistingFilenames();
        existing = v instanceof Set ? v : new Set(v || []);
      } catch { existing = null; }
    }
    setUploading(true);
    // Stato iniziale: file da caricare in "uploading", duplicati in "skipped"
    const initial = files.map((f) => {
      if (existing && existing.has(f.name.toLowerCase())) {
        return { name: f.name, size: f.size, loaded: f.size, status: "skipped", skippedReason: "già caricato" };
      }
      return { name: f.name, size: f.size, loaded: 0, status: "uploading" };
    });
    setPerFile(initial);

    let failed = [];
    let skippedCount = initial.filter((x) => x.status === "skipped").length;

    await Promise.all(files.map(async (file, idx) => {
      if (initial[idx].status === "skipped") return;
      const onProgress = (_pct, loaded, _total) => {
        setPerFile((prev) => {
          const next = [...prev];
          if (next[idx]) {
            next[idx] = { ...next[idx], loaded: loaded || next[idx].loaded };
          }
          return next;
        });
      };
      try {
        await uploadOne(file, onProgress);
        setPerFile((prev) => {
          const next = [...prev];
          if (next[idx]) next[idx] = { ...next[idx], loaded: next[idx].size, status: "done" };
          return next;
        });
      } catch (e) {
        // Estrai un messaggio d'errore leggibile: preferisci detail JSON, poi body raw, poi status HTTP.
        const status = e?.response?.status;
        const bodyRaw = e?.response?.data;
        const bodyText = typeof bodyRaw === "string"
          ? bodyRaw
          : (bodyRaw?.detail || bodyRaw?.message || (bodyRaw ? JSON.stringify(bodyRaw) : ""));
        const shortBody = (bodyText || "").slice(0, 200);
        const msg = status
          ? `HTTP ${status}${shortBody ? ` · ${shortBody}` : ""}`
          : (e?.message || "errore");
        failed.push(`${file.name}: ${msg}`);
        setPerFile((prev) => {
          const next = [...prev];
          if (next[idx]) next[idx] = { ...next[idx], status: "error", error: msg };
          return next;
        });
      }
    }));

    setUploading(false);
    // Lascia visibile lo stato finale per 2 secondi e poi resetta (a meno che ci siano errori)
    if (failed.length === 0) {
      setTimeout(() => setPerFile([]), 1500);
    }
    if (onAfter) await onAfter();

    const ok = files.length - failed.length - skippedCount;
    // Toast riassuntivo: distinguo caricati / saltati / errori
    const parts = [];
    if (ok > 0) parts.push(`${ok} ${ok === 1 ? successLabel : successLabelPlural}`);
    if (skippedCount > 0) parts.push(`${skippedCount} già presenti`);
    if (failed.length > 0) parts.push(`${failed.length} ${failed.length === 1 ? "errore" : "errori"}`);
    if (failed.length === 0) {
      toast.success(parts.join(" · ") || "Nessun file da caricare");
    } else {
      toast.error(`${parts.join(" · ")}. ${failed.slice(0, 2).join(" · ")}${failed.length > 2 ? "…" : ""}`);
    }
  }, [uploadOne, onAfter, getExistingFilenames, successLabel, successLabelPlural]);

  const handlers = {
    onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); if (!uploading) setDragOver(true); },
    onDragEnter: (e) => { e.preventDefault(); e.stopPropagation(); if (!uploading) setDragOver(true); },
    onDragLeave: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (uploading) return;
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) uploadFiles(files);
    },
  };

  const inputProps = {
    multiple: true,
    disabled: uploading,
    onChange: (e) => { uploadFiles(e.target.files); e.target.value = ""; },
  };

  // Compatibilità con vecchio render: progress aggregato.
  const totalBytes = perFile.reduce((s, f) => s + f.size, 0);
  const loadedBytes = perFile.reduce((s, f) => s + (f.loaded || 0), 0);
  const doneCount = perFile.filter((f) => f.status === "done").length;
  const skippedCount = perFile.filter((f) => f.status === "skipped").length;
  const errorCount = perFile.filter((f) => f.status === "error").length;
  const progress = perFile.length > 0 ? {
    done: doneCount,
    skipped: skippedCount,
    errors: errorCount,
    total: perFile.length,
    currentName: (perFile.find((f) => f.status === "uploading") || perFile[0])?.name || "",
    loadedBytes,
    totalBytes,
    pct: totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 0,
  } : null;

  return { uploading, dragOver, progress, perFile, handlers, uploadFiles, inputProps };
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Progress bar AGGREGATA (mostra solo la % totale + N/M file).
 * Per il dettaglio per-file usa `<UploadDetailedProgress />`.
 */
export function UploadProgressBar({ progress }) {
  if (!progress) return null;
  const sumLabel = [
    progress.done > 0 ? `${progress.done} ok` : null,
    progress.skipped > 0 ? `${progress.skipped} già presenti` : null,
    progress.errors > 0 ? `${progress.errors} errori` : null,
  ].filter(Boolean).join(" · ");
  return (
    <div className="mt-2" data-testid="upload-progress">
      <div className="h-1.5 bg-cream2 rounded-full overflow-hidden">
        <div className="h-full bg-ink transition-all" style={{ width: `${progress.pct}%` }} />
      </div>
      <div className="text-[10px] text-slate1 mt-1 flex items-center justify-between gap-2">
        <span>
          {progress.done + progress.skipped + progress.errors} / {progress.total} completati · {progress.currentName?.slice(0, 40)}
          {sumLabel && <span className="ml-1 text-ink/60">({sumLabel})</span>}
        </span>
        {progress.totalBytes > 0 && (
          <span className="font-mono">{formatBytes(progress.loadedBytes)} / {formatBytes(progress.totalBytes)} ({progress.pct}%)</span>
        )}
      </div>
    </div>
  );
}

/**
 * Lista DETTAGLIATA per-file con barra per ognuno. Utile per file pesanti / molti file.
 * Mostra: nome, size, loaded/total, %, stato (uploading/done/error).
 */
export function UploadDetailedProgress({ perFile }) {
  if (!perFile || perFile.length === 0) return null;
  return (
    <div className="mt-3 space-y-1.5 max-h-60 overflow-auto" data-testid="upload-detailed-progress">
      {perFile.map((f, idx) => {
        const pct = f.size > 0 ? Math.round((f.loaded / f.size) * 100) : 0;
        const isDone = f.status === "done";
        const isError = f.status === "error";
        const isSkipped = f.status === "skipped";
        return (
          <div key={`${f.name}-${idx}`} className="text-[10px]" data-testid={`upload-file-${idx}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0 flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                  isError ? "bg-rose-600"
                  : isSkipped ? "bg-slate-400"
                  : isDone ? "bg-emerald-600"
                  : "bg-amber-500 animate-pulse"
                }`} />
                <span className={`truncate ${isSkipped ? "text-ink/50 line-through" : "text-ink/80"}`} title={f.name}>{f.name}</span>
              </div>
              <div className="font-mono text-slate1 shrink-0">
                {isError ? <span className="text-rose-700">errore</span>
                  : isSkipped ? <span className="text-slate-500">{f.skippedReason || "saltato"}</span>
                  : isDone ? <span className="text-emerald-700">{formatBytes(f.size)}</span>
                  : <>{formatBytes(f.loaded)} / {formatBytes(f.size)} · {pct}%</>}
              </div>
            </div>
            {!isSkipped && (
              <div className="mt-0.5 h-1 bg-cream2 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${isError ? "bg-rose-500" : isDone ? "bg-emerald-600" : "bg-ink"}`}
                  style={{ width: `${isDone ? 100 : pct}%` }}
                />
              </div>
            )}
            {isError && f.error && <div className="text-rose-700 mt-0.5">{f.error}</div>}
          </div>
        );
      })}
    </div>
  );
}
